import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Grid, Card,
  CardContent, TextField, MenuItem, Button, Tooltip, Checkbox, FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import FilterListIcon from "@material-ui/icons/FilterList";
import GroupIcon from "@material-ui/icons/Group";
import GetAppIcon from "@material-ui/icons/GetApp";
import api from "../../services/api";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasRolloutFeature, ROLLOUT_FEATURES } from "../../utils/rolloutFeatures";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    background: theme.palette.type === "dark" ? "#0B0F0C" : "radial-gradient(circle at 5% 0%, rgba(0,219,117,.08), transparent 24%), #F5F8F6",
    minHeight: "100vh",
  },
  header: {
    background: "linear-gradient(118deg, #050806 0%, #101713 62%, #142A1E 100%)",
    borderRadius: "22px",
    padding: "28px 32px",
    marginBottom: "24px",
    color: "#fff",
    boxShadow: "0 20px 48px rgba(5,8,6,.20)",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerBg: {
    position: "absolute",
    right: "-20px",
    top: "-20px",
    fontSize: "8rem",
    opacity: 0.08,
    lineHeight: 1,
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: "1.8rem",
    letterSpacing: "-0.5px",
  },
  headerSubtitle: {
    opacity: 0.75,
    fontSize: "0.9rem",
    marginTop: "4px",
  },
  filterCard: {
    borderRadius: "18px",
    marginBottom: "24px",
    background: theme.palette.type === "dark" ? "#1a1d2e" : "#fff",
    boxShadow: "0 10px 28px rgba(10,35,22,.055)",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#DEE8E2",
  },
  filterActionBar: {
    padding: "18px 22px",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.08)" : "#E3EAE6"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  filterActionTitle: { fontWeight: 800, fontSize: "1rem", color: theme.palette.type === "dark" ? "#fff" : "#17231C" },
  filterActionSub: { fontSize: ".78rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,.55)" : "#66736B", marginTop: 2 },
  exportBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#EDF7F1" : "#263A2F",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,.2)" : "#CBD8D0",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#fff",
  },
  filterBody: { padding: "22px" },
  filterPanel: {
    height: "100%",
    padding: "18px",
    borderRadius: 14,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.025)" : "#F8FAF9",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#E3EAE6"}`,
  },
  panelEyebrow: { fontSize: ".7rem", fontWeight: 800, color: "#008C4A", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 5 },
  panelTitle: { fontSize: ".95rem", fontWeight: 800, color: theme.palette.type === "dark" ? "#fff" : "#1B2921", marginBottom: 3 },
  panelHelp: { fontSize: ".76rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,.52)" : "#6D7972", marginBottom: 16 },
  applyArea: { marginTop: 16, paddingTop: 16, borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.08)" : "#E3EAE6"}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" },
  applyNote: { fontSize: ".76rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,.52)" : "#6D7972", maxWidth: 650 },
  filterTitle: {
    fontWeight: 700,
    color: "#008C4A",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  filterHint: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#666",
    marginTop: "10px",
    fontStyle: "italic",
  },
  scheduleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  scheduleLabel: {
    minWidth: 90,
    fontWeight: 700,
    fontSize: "0.82rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.75)" : "#444",
  },
  applyBtn: {
    background: "#00DB75",
    color: "#04130C",
    borderRadius: "10px",
    fontWeight: 800,
    textTransform: "none",
    minHeight: 42,
    padding: "0 28px",
    boxShadow: "0 7px 18px rgba(0,219,117,.20)",
    "&:hover": { background: "#00C96C" },
  },

  // ── Podium ───────────────────────────────────────────────────────
  podiumSection: {
    marginBottom: "32px",
    padding: "22px 22px 0",
    borderRadius: 20,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#E1E9E4"}`,
    background: theme.palette.background.paper,
    boxShadow: "0 10px 30px rgba(10,35,22,.055)",
  },
  podiumSectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.8)" : "#333",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  podiumStage: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "8px",
    padding: "0 16px 0",
  },
  podiumSlot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: "1 1 0",
    maxWidth: 200,
  },
  podiumAvatar: {
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    marginBottom: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
    flexShrink: 0,
    position: "relative",
  },
  podiumName: {
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 4,
    lineHeight: 1.2,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    color: theme.palette.type === "dark" ? "#fff" : "#222",
  },
  podiumMetric: {
    fontSize: "0.78rem",
    textAlign: "center",
    marginBottom: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#555",
  },
  podiumBlock: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 10px 0 0",
    fontWeight: 800,
    fontSize: "1.5rem",
    boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
    marginTop: 12,
  },

  // Table
  tableCard: {
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(10,35,22,.06)",
    background: theme.palette.type === "dark" ? "#1a1d2e" : "#fff",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#DEE8E2",
  },
  tableHead: {
    background: theme.palette.type === "dark" ? "#101713" : "#F1F6F3",
    "& .MuiTableCell-head": {
      color: theme.palette.type === "dark" ? "#E8F5ED" : "#475467",
      fontWeight: 800,
      fontSize: "0.78rem",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.08)" : "#D8E5DD"}`,
      padding: "14px 16px",
    },
  },
  tableRow: {
    transition: "background 0.2s",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(0,219,117,.08)" : "#F4FBF7",
    },
    "& .MuiTableCell-root": {
      padding: "12px 16px",
      borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
    },
  },
  rankCell: {
    fontWeight: 800,
    fontSize: "1.1rem",
    width: "56px",
  },
  agentName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "#fff" : "#2d2d2d",
  },
  metricGood: {
    color: "#008C4A",
    fontWeight: 700,
    background: "rgba(0,219,117,.11)",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "0.85rem",
    display: "inline-block",
  },
  metricMid: {
    color: "#e65100",
    fontWeight: 700,
    background: "rgba(230,81,0,0.1)",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "0.85rem",
    display: "inline-block",
  },
  metricBad: {
    color: "#c62828",
    fontWeight: 700,
    background: "rgba(198,40,40,0.1)",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "0.85rem",
    display: "inline-block",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.8)" : "#333",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#888",
  },
  metricGuide: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12, marginBottom: 24, "@media (max-width: 760px)": { gridTemplateColumns: "1fr" } },
  guideItem: { padding: "14px 16px", borderRadius: 14, background: theme.palette.type === "dark" ? "#151C18" : "#FFFFFF", border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#E1E9E4"}`, boxShadow: "0 7px 20px rgba(10,35,22,.045)" },
  guideTitle: { fontSize: 12.5, fontWeight: 800, color: theme.palette.text.primary, marginBottom: 4 },
  guideText: { fontSize: 11.5, lineHeight: 1.45, color: theme.palette.text.secondary },
}));

const getMetricClass = (val, classes, type) => {
  const v = parseFloat(val);
  if (v === 0) return null;
  const thresholds = type === "start" ? [5, 15] : [3, 10];
  if (v <= thresholds[0]) return classes.metricGood;
  if (v <= thresholds[1]) return classes.metricMid;
  return classes.metricBad;
};

const formatMinutes = (m) => {
  const val = parseFloat(m);
  if (!val || isNaN(val) || val <= 0) return "—";
  const totalSec = Math.round(val * 60);
  if (totalSec < 60) return totalSec + " seg";
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min >= 60) {
    const h = Math.floor(min / 60);
    const remMin = min % 60;
    return remMin > 0 ? h + "h " + String(remMin).padStart(2, "0") + "m" : h + "h";
  }
  if (sec === 0) return min + " min";
  return min + "m " + sec + "s";
};

const formatAcceptanceMinutes = (m) => {
  const val = Number(m);
  if (!Number.isFinite(val) || val < 0) return "—";
  if (val < 1) return "< 1 min";
  return formatMinutes(val);
};

const exportRankingExcel = (users, filters, metricsV2) => {
  const rows = users.map((u, i) => metricsV2 ? ({
    "Posicao": i + 1,
    "Atendente": u.name,
    "Total de Atendimentos": u.totalSessions,
    "Media para Aceitar": formatAcceptanceMinutes(u.avgStartMinutes),
    "Media de Resposta": formatMinutes(u.avgResponseMinutes),
  }) : ({
    "Posicao": i + 1,
    "Atendente": u.name,
    "Total de Atendimentos": u.totalSessions,
    "Tempo p/ Iniciar": formatMinutes(u.avgStartMinutes),
    "Tempo de Resposta": formatMinutes(u.avgResponseMinutes),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 10 }, { wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 20 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Desempenho");
  const from = filters.dateFrom || ""; const to = filters.dateTo || "";
  XLSX.writeFile(wb, "desempenho_atendimento" + (from ? "_" + from : "") + ".xlsx");
};

const exportPodiumAtendimento = (users, filters) => {
  if (!users || users.length === 0) return;
  const DPR = 2, W = 1080, rowH = 56;
  const bodyRows = Math.max(0, users.length - 3);
  const H = 420 + (bodyRows > 0 ? 50 + bodyRows * rowH : 0);
  const canvas = document.createElement("canvas");
  canvas.width = W * DPR; canvas.height = H * DPR;
  const ctx = canvas.getContext("2d"); ctx.scale(DPR, DPR);
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#050806"); grad.addColorStop(0.5, "#101713"); grad.addColorStop(1, "#17442D");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
  for (let x=20;x<W;x+=55) for (let y=20;y<H;y+=55) { ctx.beginPath(); ctx.arc(x,y,1.5,0,Math.PI*2); ctx.fillStyle="rgba(255,255,255,0.05)"; ctx.fill(); }
  ctx.fillStyle = "rgba(179,136,255,0.5)"; ctx.fillRect(0, 0, W, 6);
  ctx.font = "bold 40px Arial,sans-serif"; ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("RANKING DE DESEMPENHO", W/2, 58);
  ctx.font = "18px Arial,sans-serif"; ctx.fillStyle = "rgba(255,255,255,0.6)";
  const from = filters.dateFrom || ""; const to = filters.dateTo || "";
  ctx.fillText(from && to ? from + "  —  " + to : "Ultimos 30 dias", W/2, 100);
  ctx.strokeStyle = "rgba(255,255,255,0.15)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(80, 128); ctx.lineTo(W-80, 128); ctx.stroke();
  const mColors = [{circle:"#f59e0b",label:"1"},{circle:"#90a4ae",label:"2"},{circle:"#e64a19",label:"3"}];
  const top3 = users.filter((u) => u.rankingStatus === "eligible").slice(0, 3);
  const cW = top3.length===1?420:top3.length===2?460:310, gap=20;
  const totalW = top3.length*cW+(top3.length-1)*gap, sX=(W-totalW)/2, cY=148, cH=144;
  const rrp=(x,y,w,h,r)=>{ctx.beginPath();ctx.moveTo(x+r,y);ctx.lineTo(x+w-r,y);ctx.quadraticCurveTo(x+w,y,x+w,y+r);ctx.lineTo(x+w,y+h-r);ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);ctx.lineTo(x+r,y+h);ctx.quadraticCurveTo(x,y+h,x,y+h-r);ctx.lineTo(x,y+r);ctx.quadraticCurveTo(x,y,x+r,y);ctx.closePath();};
  top3.forEach((u, i) => {
    const cx = sX+i*(cW+gap), mc = mColors[i];
    ctx.shadowColor="rgba(0,0,0,0.35)"; ctx.shadowBlur=18; ctx.shadowOffsetY=5;
    ctx.fillStyle="rgba(255,255,255,0.1)"; rrp(cx,cY,cW,cH,18); ctx.fill();
    ctx.shadowColor="transparent"; ctx.shadowBlur=0; ctx.shadowOffsetY=0;
    ctx.strokeStyle="rgba(255,255,255,0.2)"; ctx.lineWidth=1.5; rrp(cx,cY,cW,cH,18); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx+46,cY+46,30,0,Math.PI*2); ctx.fillStyle=mc.circle; ctx.fill();
    ctx.font="bold 20px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="center"; ctx.textBaseline="middle";
    ctx.fillText(mc.label+"º",cx+46,cY+46);
    const mxLen=Math.floor(cW/13), nm=u.name.length>mxLen?u.name.substring(0,mxLen-1)+"…":u.name;
    ctx.font="bold 19px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="left"; ctx.textBaseline="middle";
    ctx.fillText(nm,cx+88,cY+34);
    ctx.font="bold 22px Arial,sans-serif"; ctx.fillStyle="#ce93d8";
    ctx.fillText(formatMinutes(u.avgResponseMinutes)+" resp.",cx+88,cY+64);
    ctx.font="14px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.55)";
    ctx.fillText(u.totalSessions+" atend.",cx+88,cY+90);
    ctx.font="13px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.38)";
    ctx.fillText("Inicio: "+formatMinutes(u.avgStartMinutes),cx+88,cY+114);
  });
  if (users.length > 3) {
    const lY = cY+cH+42;
    ctx.font="bold 12px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.38)"; ctx.textAlign="left"; ctx.textBaseline="middle";
    ctx.fillText("DEMAIS POSIÇÕES", 80, lY-16);
    users.slice(3).forEach((u, i) => {
      const ry = lY+i*rowH;
      ctx.fillStyle=i%2===0?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.04)"; rrp(60,ry,W-120,rowH-8,10); ctx.fill();
      ctx.font="bold 14px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.5)"; ctx.textAlign="center";
      ctx.fillText((i+4)+"º",100,ry+(rowH-8)/2);
      const n36=u.name.length>36?u.name.substring(0,35)+"…":u.name;
      ctx.font="15px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="left";
      ctx.fillText(n36,130,ry+(rowH-8)/2);
      ctx.font="bold 15px Arial,sans-serif"; ctx.fillStyle="#ce93d8"; ctx.textAlign="right";
      ctx.fillText(formatMinutes(u.avgResponseMinutes),W-80,ry+(rowH-8)/2);
    });
  }
  ctx.font="13px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.28)"; ctx.textAlign="center"; ctx.textBaseline="middle";
  ctx.fillText("Estratiza Hub", W/2, H-22);
  const link = document.createElement("a");
  link.download = "podio_desempenho" + (from ? "_" + from : "") + ".png";
  link.href = canvas.toDataURL("image/png"); link.click();
};

// ─── Podium slot configs — order: 1st (left/tallest) | 2nd | 3rd (right/shortest) ──
const PODIUM_CONFIG = [
  {
    blockHeight: 120,
    avatarSize: 76,
    blockBg: "linear-gradient(180deg,#e65100,#ff8f00)",
    blockLabel: "🥇",
    avatarBg: "linear-gradient(135deg,#f57f17,#ffa000)",
    glowColor: "rgba(245,127,23,0.7)",
    label: "1°",
  },
  {
    blockHeight: 84,
    avatarSize: 62,
    blockBg: "linear-gradient(180deg,#546e7a,#78909c)",
    blockLabel: "🥈",
    avatarBg: "linear-gradient(135deg,#455a64,#78909c)",
    glowColor: "rgba(120,144,156,0.6)",
    label: "2°",
  },
  {
    blockHeight: 56,
    avatarSize: 52,
    blockBg: "linear-gradient(180deg,#bf360c,#d84315)",
    blockLabel: "🥉",
    avatarBg: "linear-gradient(135deg,#bf360c,#e64a19)",
    glowColor: "rgba(191,54,12,0.6)",
    label: "3°",
  },
];

const getRankEmoji = (i) => {
  if (i === 0) return "🥇";
  if (i === 1) return "🥈";
  if (i === 2) return "🥉";
  return `${i + 1}°`;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// ─── Schedule row sub-component ──────────────────────────────────────────────
const ScheduleRow = ({ label, enabled, onToggle, start, onStart, end, onEnd }) => {
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
      <FormControlLabel
        style={{ minWidth: 120, margin: 0 }}
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            color="primary"
            size="small"
          />
        }
        label={<span style={{ fontWeight: 700, fontSize: "0.83rem" }}>{label}</span>}
      />
      <TextField
        select
        size="small"
        label="Início"
        value={start}
        onChange={(e) => onStart(e.target.value)}
        variant="outlined"
        disabled={!enabled}
        style={{ width: 110 }}
      >
        {HOURS.map(h => (
          <MenuItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Fim"
        value={end}
        onChange={(e) => onEnd(e.target.value)}
        variant="outlined"
        disabled={!enabled}
        style={{ width: 110 }}
      >
        {HOURS.map(h => (
          <MenuItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const Ranking = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const metricsV2 = hasRolloutFeature(user, ROLLOUT_FEATURES.METRICS_V2);
  const [data, setData] = useState({ users: [], queues: [] });
  const [loading, setLoading] = useState(true);

  // Schedule state
  const [wdEnabled, setWdEnabled] = useState(true);
  const [wdStart, setWdStart] = useState("8");
  const [wdEnd, setWdEnd] = useState("18");
  const [satEnabled, setSatEnabled] = useState(false);
  const [satStart, setSatStart] = useState("9");
  const [satEnd, setSatEnd] = useState("13");
  const [sunEnabled, setSunEnabled] = useState(false);
  const [sunStart, setSunStart] = useState("0");
  const [sunEnd, setSunEnd] = useState("23");

  const [queueId, setQueueId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    wdEnabled: true, wdStart: "8", wdEnd: "18",
    satEnabled: false, satStart: "9", satEnd: "13",
    sunEnabled: false, sunStart: "0", sunEnd: "23",
    queueId: "all", dateFrom: "", dateTo: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const f = appliedFilters;
      const params = {
        wdEnabled: f.wdEnabled ? "1" : "0",
        wdStart: f.wdStart,
        wdEnd: f.wdEnd,
        satEnabled: f.satEnabled ? "1" : "0",
        satStart: f.satStart,
        satEnd: f.satEnd,
        sunEnabled: f.sunEnabled ? "1" : "0",
        sunStart: f.sunStart,
        sunEnd: f.sunEnd,
        // backward compat: send startHour/endHour = weekday hours
        startHour: f.wdStart,
        endHour: f.wdEnd,
        queueId: f.queueId,
      };
      if (f.dateFrom) params.dateFrom = f.dateFrom;
      if (f.dateTo) params.dateTo = f.dateTo;
      const { data: result } = await api.get("/performance/ranking", { params });
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyFilter = () => {
    setAppliedFilters({
      wdEnabled, wdStart, wdEnd,
      satEnabled, satStart, satEnd,
      sunEnabled, sunStart, sunEnd,
      queueId, dateFrom, dateTo,
    });
  };

  const users = data.users || [];
  const top3 = users.filter((u) => u.rankingStatus === "eligible").slice(0, 3);
  const podiumConfigOrder = PODIUM_CONFIG.slice(0, top3.length);

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.headerBg}>🏆</Typography>
        <Box>
          <Typography className={classes.headerTitle}>Métricas de Atendimento</Typography>
          <Typography className={classes.headerSubtitle}>
            {metricsV2
              ? "Aceite medido de Aguardando até o atendente aceitar — sem depender do envio de mensagem"
              : "Ranking baseado em tempo médio de resposta — quanto menor, melhor"}
          </Typography>
        </Box>
        <EmojiEventsIcon style={{ fontSize: "3rem", opacity: 0.6 }} />
      </Box>

      {/* Filters */}
      <Card className={classes.filterCard}>
        <Box className={classes.filterActionBar}>
          <Box>
            <Typography className={classes.filterActionTitle}>Configuração da análise</Typography>
            <Typography className={classes.filterActionSub}>Defina o recorte da operação e atualize o ranking.</Typography>
          </Box>
          <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
            <Button className={classes.exportBtn} variant="outlined" size="small" startIcon={<GetAppIcon />}
              onClick={() => exportRankingExcel(users, appliedFilters, metricsV2)}>
              Exportar Excel
            </Button>
            <Button className={classes.exportBtn} variant="outlined" size="small" startIcon={<GetAppIcon />}
              onClick={() => exportPodiumAtendimento(users, appliedFilters)}>
              Exportar pódio
            </Button>
          </Box>
        </Box>
        <CardContent className={classes.filterBody}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Box className={classes.filterPanel}>
                <Typography className={classes.panelEyebrow}>Período e fila</Typography>
                <Typography className={classes.panelTitle}>Recorte dos atendimentos</Typography>
                <Typography className={classes.panelHelp}>Escolha as datas e, se necessário, limite a análise a uma fila.</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Data início" type="date"
                      value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} variant="outlined"
                      InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Data fim" type="date"
                      value={dateTo} onChange={(e) => setDateTo(e.target.value)} variant="outlined"
                      InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField select fullWidth size="small" label="Filtrar por fila"
                      value={queueId} onChange={(e) => setQueueId(e.target.value)} variant="outlined">
                      <MenuItem value="all">Todas as filas</MenuItem>
                      {(data.queues || []).map(q => (
                        <MenuItem key={q.id} value={String(q.id)}>{q.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box className={classes.filterPanel}>
              <Typography className={classes.panelEyebrow}>Horário comercial considerado</Typography>
              <Typography className={classes.panelTitle}>Janelas válidas para o cálculo</Typography>
              <Typography className={classes.panelHelp}>Ative os dias que entram na métrica e informe o início e o fim de cada jornada.</Typography>
              <ScheduleRow
                label="Seg — Sex"
                enabled={wdEnabled}
                onToggle={setWdEnabled}
                start={wdStart} onStart={setWdStart}
                end={wdEnd} onEnd={setWdEnd}
              />
              <ScheduleRow
                label="Sábado"
                enabled={satEnabled}
                onToggle={setSatEnabled}
                start={satStart} onStart={setSatStart}
                end={satEnd} onEnd={setSatEnd}
              />
              <ScheduleRow
                label="Domingo"
                enabled={sunEnabled}
                onToggle={setSunEnabled}
                start={sunStart} onStart={setSunStart}
                end={sunEnd} onEnd={setSunEnd}
              />
              </Box>
            </Grid>
          </Grid>
          <Box className={classes.applyArea}>
            <Typography className={classes.applyNote}>
              Dias desmarcados não são contabilizados. Sem data definida, o painel considera automaticamente os últimos 30 dias.
            </Typography>
            <Button className={classes.applyBtn} onClick={applyFilter} variant="contained">
              Aplicar filtros
            </Button>
          </Box>
        </CardContent>
      </Card>

      {!metricsV2 && <Box className={classes.metricGuide}>
        <div className={classes.guideItem}>
          <Typography className={classes.guideTitle}>Total de atendimentos</Typography>
          <Typography className={classes.guideText}>Quantidade de conversas válidas atribuídas ao atendente no período.</Typography>
        </div>
        <div className={classes.guideItem}>
          <Typography className={classes.guideTitle}>Média iniciar</Typography>
          <Typography className={classes.guideText}>Tempo desde a entrada em Aguardando até o atendente iniciar o atendimento.</Typography>
        </div>
        <div className={classes.guideItem}>
          <Typography className={classes.guideTitle}>Média resposta</Typography>
          <Typography className={classes.guideText}>Espera média do cliente, considerando somente os dias e horários comerciais selecionados.</Typography>
        </div>
      </Box>}

      {loading ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: "80px" }}>
          <CircularProgress style={{ color: "#00A85A" }} />
        </Box>
      ) : users.length === 0 ? (
        <Box className={classes.emptyState}>
          <EmojiEventsIcon style={{ fontSize: "3rem", color: "#ccc" }} />
          <Typography style={{ marginTop: "12px", color: "#aaa" }}>
            Nenhum dado encontrado para os filtros selecionados
          </Typography>
        </Box>
      ) : (
        <>
          {/* ── Podium ── */}
          {top3.length > 0 && (
            <Box className={classes.podiumSection}>
              <Typography className={classes.podiumSectionTitle}>
                <EmojiEventsIcon style={{ color: "#f57f17" }} /> Pódio — Menor tempo de resposta
              </Typography>

              {/* Stage */}
              <Box className={classes.podiumStage}>
                {top3.map((user, i) => {
                  const cfg = podiumConfigOrder[i];
                  if (!user) return null;
                  const resp = parseFloat(user.avgResponseMinutes);
                  return (
                    <Box key={user.id} className={classes.podiumSlot}>
                      {/* Avatar circle */}
                      <Box className={classes.podiumAvatar}
                        style={{
                          width: cfg.avatarSize,
                          height: cfg.avatarSize,
                          background: cfg.avatarBg,
                          boxShadow: `0 0 0 4px ${cfg.glowColor}, 0 6px 20px rgba(0,0,0,0.25)`,
                        }}>
                        <Typography style={{ fontSize: cfg.avatarSize * 0.42, lineHeight: 1, color: "#fff" }}>
                          {cfg.blockLabel}
                        </Typography>
                      </Box>

                      {/* Name */}
                      <Typography className={classes.podiumName}
                        style={{ width: "100%", maxWidth: 160, fontSize: i === 0 ? "1rem" : "0.88rem" }}>
                        {user.name}
                      </Typography>

                      {/* Metrics */}
                      <Typography className={classes.podiumMetric}>
                        {resp > 0 ? `⏱ ${formatMinutes(user.avgResponseMinutes)} resp.` : "Sem dados"}
                      </Typography>
                      <Typography className={classes.podiumMetric}>
                        {user.totalSessions} atend.
                      </Typography>

                      {/* Podium block */}
                      <Box className={classes.podiumBlock}
                        style={{ height: cfg.blockHeight, background: cfg.blockBg }}>
                        <Typography style={{ fontWeight: 800, fontSize: "1.4rem", color: "rgba(255,255,255,0.9)" }}>
                          {cfg.label}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Background glow effect */}
              <Box style={{
                background: "linear-gradient(180deg, rgba(0,219,117,.11) 0%, transparent 100%)",
                borderRadius: "0 0 16px 16px",
                height: 8,
                marginTop: -1,
              }} />
            </Box>
          )}
          {top3.length === 0 && users.some((u) => u.totalSessions > 0) && (
            <Box className={classes.emptyState} style={{ marginBottom: 24 }}>
              <Typography style={{ color: "#777", fontWeight: 600 }}>
                Pódio ainda sem participantes elegíveis
              </Typography>
              <Typography style={{ color: "#999", fontSize: 13, marginTop: 4 }}>
                São necessários pelo menos 5 atendimentos válidos no período.
              </Typography>
            </Box>
          )}

          {/* ── Full table ── */}
          <Typography className={classes.sectionTitle} style={{ marginBottom: "12px" }}>
            <GroupIcon style={{ color: "#00A85A" }} /> Ranking Completo — {users.length} atendente{users.length !== 1 ? "s" : ""}
          </Typography>
          <Card className={classes.tableCard}>
            <TableContainer>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell>Pos.</TableCell>
                    <TableCell>Atendente</TableCell>
                    <TableCell align="center">Total de atendimentos</TableCell>
                    <TableCell align="center">
                      <Tooltip title={metricsV2 ? "Tempo médio entre a mensagem que colocou o atendimento em Aguardando e o aceite pelo atendente. Não depende do envio de uma resposta." : "Tempo médio desde que o ticket entrou em Aguardando até o atendente aceitar e enviar a 1ª mensagem ao cliente. Mede o quanto rápido o colaborador inicia o atendimento."} placement="top" arrow>
                        <Box>
                          <span style={{ cursor: "help" }}>{metricsV2 ? "Média para aceitar" : "Média iniciar"}</span>
                          <Typography style={{ fontSize: 10, color: "#667085", lineHeight: 1.2, fontWeight: 500, marginTop: 1 }}>
                            {metricsV2 ? "Aguardando → aceite" : "Tempo do 1º contato: de Aguardando até 1ª msg"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Tempo médio que o cliente esperou por cada resposta, contando somente os dias e horários selecionados. Intervalos acima de 12 horas comerciais não entram nesta média." placement="top" arrow>
                        <Box>
                          <span style={{ cursor: "help" }}>Média resposta</span>
                          <Typography style={{ fontSize: 10, color: "#667085", lineHeight: 1.2, fontWeight: 500, marginTop: 1 }}>
                            Espera média do cliente por resposta
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, i) => {
                    const respClass = getMetricClass(user.avgResponseMinutes, classes, "response");
                    const startClass = getMetricClass(user.avgStartMinutes, classes, "start");
                    return (
                      <TableRow key={user.id} className={classes.tableRow}
                        style={i < 3 ? { background: "rgba(0,219,117,.035)" } : {}}>
                        <TableCell className={classes.rankCell}>
                          {user.rankingStatus === "eligible" ? getRankEmoji(i) : "—"}
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.agentName}>{user.name}</Typography>
                          {user.rankingStatus === "insufficient" && (
                            <Typography style={{ fontSize: 11, color: "#f57c00", fontWeight: 600 }}>
                              Amostra insuficiente (mínimo 5)
                            </Typography>
                          )}
                          {user.rankingStatus === "no_data" && (
                            <Typography style={{ fontSize: 11, color: "#aaa" }}>
                              Sem atendimentos no período
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography style={{ fontWeight: 600 }}>
                            {user.totalSessions > 0 ? user.totalSessions : <span style={{ color: "#bbb" }}>—</span>}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {metricsV2 ? (user.totalSessions > 0 ? (
                            <span className={startClass || classes.metricGood}>
                              {formatAcceptanceMinutes(user.avgStartMinutes)}
                            </span>
                          ) : (
                            <span style={{ color: "#bbb" }}>—</span>
                          )) : parseFloat(user.avgStartMinutes) > 0 ? (
                            <span className={startClass || classes.metricGood}>
                              {formatMinutes(user.avgStartMinutes)}
                            </span>
                          ) : (
                            <span style={{ color: "#bbb" }}>—</span>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {parseFloat(user.avgResponseMinutes) > 0 ? (
                            <span className={respClass || classes.metricGood}>
                              {formatMinutes(user.avgResponseMinutes)}
                            </span>
                          ) : (
                            <span style={{ color: "#bbb" }}>—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Legend */}
          <Box style={{ marginTop: "16px", display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
            <Typography style={{ fontSize: "0.78rem", color: "#888" }}>Tempo de resposta:</Typography>
            {[
              { cls: "metricGood", label: "Rápido ≤ 3 min" },
              { cls: "metricMid", label: "Moderado 3–10 min" },
              { cls: "metricBad", label: "Lento > 10 min" },
            ].map(l => (
              <span key={l.label} className={classes[l.cls]} style={{ fontSize: "0.75rem" }}>{l.label}</span>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Ranking;
